import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSeasonYear, useSeasonYearContext } from '../context/SeasonYearContext';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Paper,
  Stack,
  Grid,
  Tooltip,
  Menu,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { API_URL } from '../config';
import { authedFetch } from '../utils/apiUtils';
import ErrorAlert from './ErrorAlert';
import { ConfirmDialog } from './common/ConfirmDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublicIcon from '@mui/icons-material/Public';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface Division {
  division_id: number;
  division_code: string;
  division_name: string;
  division_description: string;
  is_active: boolean;
}

interface Region {
  region_id: number;
  region_code: string;
  region_name: string;
}

interface DivisionRegion {
  division_region_id: number;
  division_id: number;
  region_id: number;
  season_year: number;
  is_active: boolean;
}

interface DivisionFormData {
  division_id?: number;
  division_code: string;
  division_name: string;
  division_description: string;
  is_active: boolean;
}

interface NewRegionAssignment {
  region_id: number | string;
  season_year: number;
  is_active: boolean;
}

interface ErrorObject {
  message: string;
  details?: string;
}

type ErrorState = ErrorObject | string | null;

const emptyDivision: DivisionFormData = {
  division_code: '',
  division_name: '',
  division_description: '',
  is_active: true,
};

function DivisionManagement(): React.ReactElement {
  const configuredSeasonYear = useSeasonYear();
  const { setSeasonYear: setGlobalSeasonYear } = useSeasonYearContext();

  const [divisions, setDivisions] = useState<Division[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [divisionRegions, setDivisionRegions] = useState<DivisionRegion[]>([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [openRegionDialog, setOpenRegionDialog] = useState(false);
  const [deleteDivisionTarget, setDeleteDivisionTarget] = useState<Division | null>(null);
  const [removeAssignmentTarget, setRemoveAssignmentTarget] = useState<DivisionRegion | null>(null);
  const [isDeletingDivision, setIsDeletingDivision] = useState(false);
  const [isRemovingAssignment, setIsRemovingAssignment] = useState(false);

  const [selectedDivision, setSelectedDivision] = useState<Division | null>(null);
  const [editingDivision, setEditingDivision] = useState<DivisionFormData | null>(null);
  const [error, setError] = useState<ErrorState>(null);
  const [seasonAnchorEl, setSeasonAnchorEl] = useState<null | HTMLElement>(null);

  const [newRegionAssignment, setNewRegionAssignment] = useState<NewRegionAssignment>({
    region_id: '',
    season_year: configuredSeasonYear,
    is_active: true,
  });

  useEffect(() => {
    setNewRegionAssignment((prev) => ({ ...prev, season_year: configuredSeasonYear }));
  }, [configuredSeasonYear]);

  const setErrorObject = (err: Error | null, customMessage: ErrorObject | null = null): void => {
    if (err) console.error(err);
    setError(customMessage || (err ? { message: err.message } : null));
  };

  const fetchDivisions = useCallback(async (): Promise<void> => {
    try {
      const response = await authedFetch(`${API_URL}/divisions`);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to fetch divisions');
      }
      const data: Division[] = await response.json();
      setDivisions(data);
    } catch (err) {
      setErrorObject(err as Error, {
        message: 'Unable to load divisions',
        details: 'Please try refreshing the page',
      });
    }
  }, []);

  const fetchRegions = useCallback(async (): Promise<void> => {
    try {
      const response = await authedFetch(`${API_URL}/regions`);
      const data = await response.json();
      if (data.error) {
        setError(data.error);
        return;
      }
      setRegions(data);
    } catch (err) {
      setErrorObject(err as Error, { message: 'Failed to fetch regions' });
    }
  }, []);

  const fetchDivisionRegions = useCallback(async (): Promise<void> => {
    try {
      const response = await authedFetch(`${API_URL}/division-regions`);
      const data = await response.json();
      if (data.error) {
        setError(data.error);
        return;
      }
      setDivisionRegions(data);
    } catch (err) {
      setErrorObject(err as Error, { message: 'Failed to fetch division regions' });
    }
  }, []);

  useEffect(() => {
    fetchDivisions();
    fetchRegions();
    fetchDivisionRegions();
  }, [fetchDivisions, fetchRegions, fetchDivisionRegions]);

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    divisionRegions.forEach((dr) => years.add(dr.season_year));
    years.add(configuredSeasonYear);
    return Array.from(years).sort((a, b) => b - a);
  }, [divisionRegions, configuredSeasonYear]);

  const stats = useMemo(() => {
    const total = divisions.length;
    const active = divisions.filter((d) => d.is_active).length;
    const assignmentsThisSeason = divisionRegions.filter(
      (dr) => dr.season_year === configuredSeasonYear,
    ).length;
    return [
      { label: 'Total Divisions', value: total.toLocaleString(), icon: <CategoryIcon /> },
      { label: 'Active', value: active.toLocaleString(), icon: <CheckCircleIcon />, tone: 'success' as const },
      { label: 'Region Assignments', value: assignmentsThisSeason.toLocaleString(), icon: <PublicIcon /> },
    ];
  }, [divisions, divisionRegions, configuredSeasonYear]);

  const getRegionAssignmentsForDivision = (divisionId: number): DivisionRegion[] =>
    divisionRegions.filter(
      (dr) => dr.division_id === divisionId && dr.season_year === configuredSeasonYear,
    );

  const validateDivision = (division: DivisionFormData): string | null => {
    if (!division.division_code) return 'Division code is required';
    if (!division.division_name) return 'Division name is required';
    return null;
  };

  const handleAddOrUpdate = async (): Promise<void> => {
    if (!editingDivision) return;
    const validationError = validateDivision(editingDivision);
    if (validationError) {
      setError({ message: 'Validation error', details: validationError });
      return;
    }

    try {
      const isEdit = Boolean(editingDivision.division_id);
      const url = isEdit
        ? `${API_URL}/divisions/${editingDivision.division_id}`
        : `${API_URL}/divisions`;
      const response = await authedFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingDivision),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error?.message || data.error || 'Failed to save division');
      }
      const saved: Division = await response.json();
      setDivisions((prev) =>
        isEdit ? prev.map((d) => (d.division_id === saved.division_id ? saved : d)) : [...prev, saved],
      );
      setEditingDivision(null);
      setOpenDialog(false);
    } catch (err) {
      setErrorObject(err as Error, { message: 'Failed to save division' });
    }
  };

  const confirmDeleteDivision = async (): Promise<void> => {
    if (!deleteDivisionTarget) return;
    setIsDeletingDivision(true);
    try {
      const response = await authedFetch(
        `${API_URL}/divisions/${deleteDivisionTarget.division_id}`,
        { method: 'DELETE' },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete division');
      }
      setDivisions((prev) =>
        prev.filter((d) => d.division_id !== deleteDivisionTarget.division_id),
      );
      setDivisionRegions((prev) =>
        prev.filter((dr) => dr.division_id !== deleteDivisionTarget.division_id),
      );
      setDeleteDivisionTarget(null);
    } catch (err) {
      setErrorObject(err as Error, { message: 'Failed to delete division' });
    } finally {
      setIsDeletingDivision(false);
    }
  };

  const handleAddRegionToDivision = async (): Promise<void> => {
    if (!selectedDivision || !newRegionAssignment.region_id) {
      setError('Please select a region');
      return;
    }
    try {
      const response = await authedFetch(`${API_URL}/division-regions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          division_id: selectedDivision.division_id,
          ...newRegionAssignment,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to assign region');
      }
      setDivisionRegions((prev) => [...prev, data]);
      setOpenRegionDialog(false);
      setNewRegionAssignment({
        region_id: '',
        season_year: configuredSeasonYear,
        is_active: true,
      });
    } catch (err) {
      setErrorObject(err as Error, { message: 'Failed to assign region' });
    }
  };

  const confirmRemoveAssignment = async (): Promise<void> => {
    if (!removeAssignmentTarget) return;
    setIsRemovingAssignment(true);
    try {
      const response = await authedFetch(
        `${API_URL}/division-regions/${removeAssignmentTarget.division_region_id}`,
        { method: 'DELETE' },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to remove region');
      }
      setDivisionRegions((prev) =>
        prev.filter(
          (dr) => dr.division_region_id !== removeAssignmentTarget.division_region_id,
        ),
      );
      setRemoveAssignmentTarget(null);
    } catch (err) {
      setErrorObject(err as Error, { message: 'Failed to remove region' });
    } finally {
      setIsRemovingAssignment(false);
    }
  };

  const openAddDivision = (): void => {
    setEditingDivision({ ...emptyDivision });
    setOpenDialog(true);
  };

  const openEditDivision = (division: Division): void => {
    setEditingDivision({ ...division });
    setOpenDialog(true);
  };

  const handleRegionSelectChange = (e: SelectChangeEvent<string | number>): void => {
    setNewRegionAssignment({ ...newRegionAssignment, region_id: e.target.value });
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Divisions
          </Typography>
          <Button
            onClick={(e) => setSeasonAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 9999,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '0.8125rem',
              py: 0.25,
              px: 1.5,
              minHeight: 28,
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.disabled' },
            }}
          >
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ mr: 0.75, fontWeight: 500 }}
            >
              Season
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
              {configuredSeasonYear}
            </Typography>
          </Button>
          <Menu
            anchorEl={seasonAnchorEl}
            open={Boolean(seasonAnchorEl)}
            onClose={() => setSeasonAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 100 } } }}
          >
            {yearOptions.map((y) => (
              <MenuItem
                key={y}
                selected={y === configuredSeasonYear}
                onClick={() => {
                  setGlobalSeasonYear(y);
                  setSeasonAnchorEl(null);
                }}
              >
                {y}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDivision}>
          Add Division
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid key={stat.label} item xs={12} sm={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {stat.label}
                  </Typography>
                  <Box
                    sx={{
                      color: stat.tone === 'success' ? 'success.main' : 'text.disabled',
                      display: 'flex',
                      alignItems: 'center',
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
                >
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack spacing={2}>
        {divisions.map((division) => {
          const assignments = getRegionAssignmentsForDivision(division.division_id);
          return (
            <Paper key={division.division_id} sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                  mb: division.division_description ? 1.5 : 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.25 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {division.division_name}
                    </Typography>
                    <Chip
                      label={division.division_code}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', letterSpacing: '0.04em' }}
                    />
                    {!division.is_active && (
                      <Chip label="Inactive" size="small" color="default" variant="outlined" sx={{ height: 20 }} />
                    )}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => openEditDivision(division)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover, &.Mui-focusVisible': { color: 'primary.main' },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteDivisionTarget(division)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover, &.Mui-focusVisible': { color: 'error.main' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              {division.division_description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {division.division_description}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: '0.04em', fontWeight: 500 }}
                >
                  Regions
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => {
                    setSelectedDivision(division);
                    setNewRegionAssignment({
                      region_id: '',
                      season_year: configuredSeasonYear,
                      is_active: true,
                    });
                    setOpenRegionDialog(true);
                  }}
                >
                  Assign region
                </Button>
              </Box>
              {assignments.length === 0 ? (
                <Typography variant="body2" color="text.disabled">
                  No regions assigned for {configuredSeasonYear}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {assignments.map((dr) => {
                    const region = regions.find((r) => r.region_id === dr.region_id);
                    if (!region) return null;
                    return (
                      <Chip
                        key={dr.division_region_id}
                        label={region.region_name}
                        size="small"
                        variant="outlined"
                        color={dr.is_active ? 'primary' : 'default'}
                        onDelete={() => setRemoveAssignmentTarget(dr)}
                      />
                    );
                  })}
                </Box>
              )}
            </Paper>
          );
        })}
        {divisions.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No divisions yet. Add one to get started.</Typography>
          </Paper>
        )}
      </Stack>

      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setEditingDivision(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingDivision?.division_id ? 'Edit Division' : 'Add Division'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Code"
              fullWidth
              required
              value={editingDivision?.division_code ?? ''}
              onChange={(e) =>
                setEditingDivision((prev) => prev && { ...prev, division_code: e.target.value })
              }
            />
            <TextField
              size="small"
              label="Name"
              fullWidth
              required
              value={editingDivision?.division_name ?? ''}
              onChange={(e) =>
                setEditingDivision((prev) => prev && { ...prev, division_name: e.target.value })
              }
            />
            <TextField
              size="small"
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={editingDivision?.division_description ?? ''}
              onChange={(e) =>
                setEditingDivision(
                  (prev) => prev && { ...prev, division_description: e.target.value },
                )
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editingDivision?.is_active ?? true}
                  onChange={(e) =>
                    setEditingDivision(
                      (prev) => prev && { ...prev, is_active: e.target.checked },
                    )
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => {
              setOpenDialog(false);
              setEditingDivision(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleAddOrUpdate} variant="contained">
            {editingDivision?.division_id ? 'Save changes' : 'Add division'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openRegionDialog}
        onClose={() => setOpenRegionDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Assign region to {selectedDivision?.division_name}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Region</InputLabel>
              <Select
                value={newRegionAssignment.region_id}
                label="Region"
                onChange={handleRegionSelectChange}
              >
                {regions.map((region) => (
                  <MenuItem key={region.region_id} value={region.region_id}>
                    {region.region_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Season Year"
              type="number"
              value={newRegionAssignment.season_year}
              onChange={(e) =>
                setNewRegionAssignment({
                  ...newRegionAssignment,
                  season_year: parseInt(e.target.value, 10) || configuredSeasonYear,
                })
              }
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newRegionAssignment.is_active}
                  onChange={(e) =>
                    setNewRegionAssignment({
                      ...newRegionAssignment,
                      is_active: e.target.checked,
                    })
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setOpenRegionDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddRegionToDivision} variant="contained">
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteDivisionTarget)}
        title="Delete Division"
        message={
          deleteDivisionTarget
            ? `Delete ${deleteDivisionTarget.division_name}? This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
        loading={isDeletingDivision}
        onConfirm={confirmDeleteDivision}
        onCancel={() => setDeleteDivisionTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(removeAssignmentTarget)}
        title="Remove Region Assignment"
        message={
          removeAssignmentTarget
            ? `Remove this region assignment for season ${removeAssignmentTarget.season_year}?`
            : ''
        }
        confirmText="Remove"
        cancelText="Cancel"
        severity="warning"
        loading={isRemovingAssignment}
        onConfirm={confirmRemoveAssignment}
        onCancel={() => setRemoveAssignmentTarget(null)}
      />

      <ErrorAlert error={error} onClose={() => setError(null)} />
    </Box>
  );
}

export default DivisionManagement;
